import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal, ChapterLabel } from "./primitives";
import { STATS, ABOUT_IMG } from "../../data/site";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1600;
    const startTime = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.floor(eased * value));
      if (p < 1) requestAnimationFrame(tick);
      else setDisplay(value);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  const formatted =
    value >= 1000 ? display.toLocaleString("it-IT") : display.toString();

  return (
    <span ref={ref} className="font-display text-6xl font-bold tracking-tight text-[#FAFAFA] md:text-7xl">
      {formatted}
      <span className="text-[#FF2A2A]">{suffix}</span>
    </span>
  );
};

const HIGHLIGHTS = [
  "Oltre 50 anni di esperienza",
  "Personale qualificato",
  "Strumenti moderni",
  "Attenzione alla sicurezza",
];

export const About = () => {
  return (
    <section
      id="chi-siamo"
      data-testid="about-section"
      className="relative bg-[#0A0A0A] px-5 py-24 md:px-10 md:py-32"
    >
      <div className="mx-auto max-w-[1400px]">
        <ChapterLabel number="01" title="La nostra esperienza al tuo servizio" />

        <div className="grid grid-cols-1 gap-px lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="flex h-full flex-col justify-between gap-10 border border-white/10 p-8 md:p-12">
              <p className="font-body text-lg leading-relaxed text-[#A1A1AA] md:text-xl">
                L'autofficina nasce nel 1972 dalla passione per il mondo dei
                motori di <span className="text-[#FAFAFA]">Ciro Natale</span>,
                poi trasmessa al figlio{" "}
                <span className="text-[#FAFAFA]">Pasquale</span>. Nel corso
                degli anni l'officina si è evoluta integrando nuove tecnologie
                diagnostiche e mantenendo sempre un approccio basato su
                qualità, trasparenza e attenzione al cliente.
              </p>
              <ul className="grid grid-cols-1 gap-px sm:grid-cols-2">
                {HIGHLIGHTS.map((h, i) => (
                  <li
                    key={i}
                    data-testid={`about-highlight-${i}`}
                    className="flex items-center gap-3 border border-white/10 px-5 py-4"
                  >
                    <span className="h-2 w-2 shrink-0 bg-[#FF2A2A]" />
                    <span className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#FAFAFA]">
                      {h}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="group relative h-72 overflow-hidden border border-white/10 lg:h-full">
              <img
                src={ABOUT_IMG}
                alt="Meccanico al lavoro sul motore"
                className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 to-transparent" />
              <span className="absolute bottom-6 left-6 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#FAFAFA]">
                Ciro e Pasquale Natale
              </span>
            </div>
          </Reveal>
        </div>

        <div className="mt-px grid grid-cols-1 gap-px border-t border-white/10 md:grid-cols-3">
          {STATS.map((s, i) => (
            <Reveal
              key={i}
              delay={i * 0.1}
              data-testid={`stat-${i}`}
              className="border-b border-white/10 px-8 py-12 md:border-b-0 md:border-r md:last:border-r-0"
            >
              {s.display ? (
                <span className="block whitespace-nowrap font-display text-5xl font-bold uppercase tracking-tight text-[#FAFAFA] md:text-6xl lg:text-7xl">
                  {s.display}
                </span>
              ) : (
                <Counter value={s.value} suffix={s.suffix} />
              )}
              <p className="mt-3 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">
                {s.label}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
